import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Zap, ArrowRight, Sparkles, Compass, Youtube, Check } from "lucide-react";
import { api } from "@/lib/api";
import { getIcon } from "@/lib/icons";
import { Button } from "@/components/ui/button";

export default function SharedRoadmap() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/share/${token}`)
      .then(({ data }) => setSnap(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (error || !snap) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6 text-center" data-testid="shared-roadmap-missing">
        <div>
          <h1 className="font-heading font-extrabold text-2xl">This roadmap isn't available</h1>
          <p className="mt-2 text-muted-foreground">The link may be wrong or removed.</p>
          <Button className="mt-6 bg-primary text-primary-foreground" data-testid="shared-build-own" onClick={() => navigate("/quiz")}>
            Build your own AI path <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // group modules by level label, preserving order
  const groups = [];
  snap.modules.forEach((m) => {
    let g = groups.find((x) => x.label === m.level_label);
    if (!g) { g = { label: m.level_label, items: [] }; groups.push(g); }
    g.items.push(m);
  });

  return (
    <div className="min-h-screen bg-background grid-paper" data-testid="shared-roadmap-page">
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground group-hover:rotate-6 transition-transform"><Zap className="h-5 w-5 fill-current" /></span>
            <span className="font-heading font-extrabold text-lg tracking-tight">AI Pathshaalaa</span>
          </Link>
          <Button size="sm" className="bg-primary text-primary-foreground" data-testid="shared-header-cta" onClick={() => navigate("/quiz")}>
            Build my path
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
            <Sparkles className="h-3.5 w-3.5" /> A personalized AI roadmap
          </span>
          <h1 className="mt-5 font-heading font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            {snap.name}'s path to <span className="text-primary">AI mastery</span>
          </h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-semibold"><Compass className="h-3.5 w-3.5 text-primary" /> {snap.archetype_label}</span>
            {snap.niche_label && <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-semibold">{snap.niche_label}</span>}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-semibold">{snap.language_label}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm font-semibold">{snap.module_count} modules</span>
          </div>
          {snap.why_summary && (
            <p className="mt-6 text-lg md:text-xl font-medium text-foreground/85 leading-relaxed max-w-2xl border-l-4 border-primary/30 pl-5">
              {snap.why_summary}
            </p>
          )}
        </motion.div>

        <div className="mt-10 space-y-8">
          {groups.map((g, gi) => (
            <motion.div key={g.label + gi} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: gi * 0.05 }}>
              <p className="font-mono text-xs font-bold uppercase tracking-widest text-muted-foreground">{g.label}</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {g.items.map((m, i) => {
                  const Icon = getIcon(m.icon);
                  return (
                    <div key={m.title + i} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
                      <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                      <span className="font-heading font-bold text-sm">{m.title}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* conversion CTA */}
        <div className="mt-14 rounded-3xl border border-border bg-primary text-primary-foreground p-8 md:p-10 text-center shadow-lg shadow-primary/20">
          <h2 className="font-heading font-black tracking-tight text-2xl md:text-3xl">Want a path like this — built around <em>you</em>?</h2>
          <p className="mt-3 text-primary-foreground/85 max-w-xl mx-auto">
            Take a 60-second quiz and we'll curate the best free YouTube lessons into your own structured AI course.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-sm font-medium text-primary-foreground/90">
            <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> Personalized to your goals</span>
            <span className="inline-flex items-center gap-1.5"><Youtube className="h-4 w-4" /> Videos free on YouTube</span>
          </div>
          <Button size="lg" className="mt-7 bg-white text-primary hover:bg-white/90 h-12 px-8" data-testid="shared-cta" onClick={() => navigate("/quiz")}>
            Build my AI path <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </main>
    </div>
  );
}
